import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmployeePicker from '../../components/EmployeePicker';
import Pagination from '../../components/Pagination';
import BulkActionBar from '../../components/BulkActionBar';
import SegmentedFilterBar from '../../components/SegmentedFilterBar';
import ScopeToggle from '../../components/ScopeToggle';
import { useScope } from '../../lib/hooks/useScope';
import { usePaginated } from '../../lib/hooks/usePaginated';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useSortable, SortIcon } from '../../lib/hooks/useSortable';
import { formatDate } from '../../lib/format';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Trash2, Edit2, X, Building2, Users, Target, ChevronRight, Download, Send } from 'lucide-react';
import { toast } from 'sonner';
import EntityActivityDrawer from '../../components/crm/EntityActivityDrawer';
import LocationCombobox from '../../components/LocationCombobox';

const SIZES = ['Small (< 50)', 'Mid (50 - 200)', 'Large (200 - 1000)', 'Enterprise (1000+)'];
const INDUSTRIES = ['Manufacturing', 'Pharma', 'Warehousing', 'Solar', 'Renewable', 'Data Center', 'Hospitality', 'Construction', 'Other'];
// Canonical CRM Tag list (Iteration 54) — used across Contacts / Companies / Consultants
const CRM_TAGS = ['Decision Maker', 'Influencer', 'Recommander', 'Consultant', 'Architect', 'Owner/MD', 'Govt Official', 'VIP'];

const initialForm = {
  name: '', industry: '', website: '', size: '', address: '', location: '', country: 'India',
  annual_revenue: '', owner: '', tags: [], notes: '',
};

const normalizeWebsite = (raw) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Strip leading "www." duplicates
  const stripped = trimmed.replace(/^www\./i, '');
  return `https://www.${stripped}`;
};

export default function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerCompany, setDrawerCompany] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const { scope, setScope } = useScope('companies');
  const { sorted, sortKey, sortDir, getProps } = useSortable(filteredCompanies, { defaultKey: 'created_at', defaultDir: 'desc' });

  const pagination = usePaginated(sorted, { defaultPageSize: 10 });
  const pageIds = pagination.paged.map((c) => c.id);
  const selection = useRowSelection();

  // Filter definitions
  const filterDefs = useMemo(() => {
    const industries = Array.from(new Set(companies.map((c) => c.industry).filter(Boolean))).sort();
    const sizes = Array.from(new Set(companies.map((c) => c.size).filter(Boolean))).sort();
    const locations = Array.from(new Set(companies.map((c) => c.location || [c.city, c.state].filter(Boolean).join(', ')).filter(Boolean))).sort();
    const owners = Array.from(new Set(companies.map((c) => c.owner).filter(Boolean))).sort();
    return [
      { key: 'industry', label: 'Industry', type: 'multiSelect', options: industries },
      { key: 'size', label: 'Size', type: 'multiSelect', options: sizes },
      { key: 'location', label: 'Location', type: 'multiSelect', options: locations },
      { key: 'tags', label: 'Tags', type: 'tags', options: allTags.map((t) => t.name || t) },
      { key: 'owner', label: 'Owner', type: 'multiSelect', options: owners },
    ];
  }, [companies, allTags]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} selected compan${ids.length === 1 ? 'y' : 'ies'}?\n\nThis action cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => apiDel(`/crm/companies/${id}`)));
      toast.success(`Deleted ${ids.length} compan${ids.length === 1 ? 'y' : 'ies'}`);
      selection.clear();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleBulkExport = () => {
    const rows = companies.filter((c) => selection.selected.has(c.id));
    const csv = [
      ['Company', 'Industry', 'Size', 'Location', 'Owner', 'Tags', 'Created'].join(','),
      ...rows.map((c) => [c.name, c.industry, c.size, c.location || [c.city, c.state].filter(Boolean).join(', '), c.owner, (c.tags || []).join('; '), c.created_at].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `companies-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} compan${rows.length === 1 ? 'y' : 'ies'}`);
  };

  const load = async () => {
    if (!scope) return;
    try {
      const [cRes, tRes] = await Promise.all([apiGet(`/crm/companies?scope=${scope}`), apiGet('/crm/tags').catch(() => ({ tags: [] }))]);
      setCompanies(cRes.companies || []);
      setAllTags(tRes.tags || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      // annual_revenue removed from form; drop it from any prior state
      const payload = { ...form };
      delete payload.annual_revenue;
      if (editing) {
        const updated = await apiPut(`/crm/companies/${editing.id}`, payload);
        toast.success('Updated');
        // Refresh the drawer panel if it is showing the same company
        if (drawerCompany?.id === editing.id) {
          setDrawerCompany(prev => ({ ...prev, ...updated }));
        }
      } else {
        await apiPost('/crm/companies', payload);
        toast.success('Created');
      }
      setShowForm(false); setEditing(null); setForm(initialForm); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this company?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/crm/companies/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  const openEdit = (c, e) => {
    e?.stopPropagation();
    const legacyLoc = c.location || [c.city, c.state].filter(Boolean).join(', ');
    setEditing(c);
    setForm({ ...initialForm, ...c, location: legacyLoc, tags: c.tags || [] });
    setShowForm(true);
  };

  const toggleTag = (tag) => {
    const tags = form.tags || [];
    setForm({ ...form, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag] });
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Business Development</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Companies</h1>
          <p className="text-sm text-slate-600 mt-1">Organizations you sell to. Each company can have many contacts and projects.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-company-btn">
          <Plus size={16} /> Add Company
        </button>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <ScopeToggle value={scope} onChange={setScope} testid="scope-toggle-companies" />
        <div className="text-xs text-slate-500">
          Showing <b className="text-[#1F7A6E]">{companies.length}</b> companies · <span className="uppercase tracking-wider font-display font-bold text-[10px]">{scope}</span>
        </div>
      </div>

      {/* Rich filters + saved segments */}
      <SegmentedFilterBar
        entityType="companies"
        items={companies.map((c) => ({ ...c, location: c.location || [c.city, c.state].filter(Boolean).join(', ') }))}
        filterDefs={filterDefs}
        onFiltered={setFilteredCompanies}
      />
      <div className="mb-3"></div>

      <BulkActionBar count={selection.count} onClear={selection.clear} className="mb-3">
        <button onClick={handleBulkExport} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-white/15 text-white rounded-sm hover:bg-white/25 flex items-center gap-1" data-testid="bulk-export-companies"><Download size={11} /> Export CSV</button>
        <button onClick={handleBulkDelete} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 flex items-center gap-1" data-testid="bulk-delete-companies"><Trash2 size={11} /> Delete</button>
      </BulkActionBar>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="companies-table">
        <table className="w-full dense-table">
          <thead><tr>
            <th className="w-10"><input type="checkbox" className="w-4 h-4 accent-[#1F7A6E] cursor-pointer" checked={selection.allSelected(pageIds)} ref={(el) => { if (el) el.indeterminate = selection.someSelected(pageIds); }} onChange={() => selection.toggleAll(pageIds)} data-testid="select-all-companies" /></th>
            <th {...getProps('name')}>Company <SortIcon dir={sortKey === 'name' ? sortDir : null} /></th>
            <th {...getProps('industry')}>Industry <SortIcon dir={sortKey === 'industry' ? sortDir : null} /></th>
            <th {...getProps('city')}>Location <SortIcon dir={sortKey === 'city' ? sortDir : null} /></th>
            <th {...getProps('contact_count')}>Contacts <SortIcon dir={sortKey === 'contact_count' ? sortDir : null} /></th>
            <th {...getProps('deal_count')}>Projects <SortIcon dir={sortKey === 'deal_count' ? sortDir : null} /></th>
            <th>Tags</th>
            <th {...getProps('owner')}>Owner <SortIcon dir={sortKey === 'owner' ? sortDir : null} /></th>
            <th {...getProps('created_at')}>Created <SortIcon dir={sortKey === 'created_at' ? sortDir : null} /></th>
            <th></th>
          </tr></thead>
          <tbody>
            {pagination.paged.map(c => (
              <tr key={c.id} onClick={() => setDrawerCompany(c)} className={`hover:bg-slate-50 cursor-pointer ${selection.isSelected(c.id) ? 'bg-teal-50/40' : ''}`} data-testid={`company-row-${c.id}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="w-4 h-4 accent-[#1F7A6E] cursor-pointer" checked={selection.isSelected(c.id)} onChange={() => selection.toggle(c.id)} data-testid={`select-company-${c.id}`} />
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-sm bg-teal-50 text-[#1F7A6E] flex items-center justify-center font-display font-bold text-xs">{(c.name || '?').slice(0,2).toUpperCase()}</div>
                    <div>
                      <div className="font-display font-semibold text-[#0F172A]">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.size || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="text-xs">{c.industry || '—'}</td>
                <td className="text-xs">{c.location || [c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                <td className="text-xs"><span className="inline-flex items-center gap-1"><Users size={11} /> {c.contact_count || 0}</span></td>
                <td className="text-xs"><span className="inline-flex items-center gap-1"><Target size={11} /> {c.deal_count || 0}</span></td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).slice(0, 3).map(t => <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-teal-50 text-[#1F7A6E] rounded-sm">{t}</span>)}
                  </div>
                </td>
                <td className="text-xs">{c.owner || '—'}</td>
                <td className="text-xs whitespace-nowrap">{formatDate(c.created_at)}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/app/crm/leads', {
                          state: {
                            prefill: {
                              company_id: c.id,
                              client: c.name,
                              industry: c.industry || '',
                              location: c.location || (c.city ? `${c.city}${c.state ? ', ' + c.state : ''}` : ''),
                            }
                          }
                        });
                      }}
                      className="text-emerald-600 hover:text-emerald-800"
                      title="Push to Lead Tracker"
                      data-testid={`push-to-lead-${c.id}`}
                    >
                      <Send size={14} />
                    </button>
                    <button onClick={(e) => openEdit(c, e)} className="text-slate-500 hover:text-[#1F7A6E]"><Edit2 size={14} /></button>
                    <button onClick={(e) => remove(c.id, e)} className="text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!companies.length && <tr><td colSpan="10" className="p-8 text-center text-sm text-slate-500">No companies yet. Add your first company to get started.</td></tr>}
          </tbody>
        </table>
        {companies.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination page={pagination.page} pageSize={pagination.pageSize} total={pagination.total} start={pagination.start} end={pagination.end} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} label="companies" />
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Company' : 'Add Company'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required data-testid="company-name-input" /></div>
                <div>
                  <label className="label">Industry</label>
                  <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="input">
                    <option value="">—</option>
                    {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                  </select>
                </div>
                <div><label className="label">Website</label><input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} onBlur={(e) => setForm({ ...form, website: normalizeWebsite(e.target.value) })} className="input" placeholder="example.com" /></div>
                <div>
                  <label className="label">Size</label>
                  <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="input">
                    <option value="">—</option>
                    {SIZES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Location</label>
                  <LocationCombobox value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="company-location" />
                </div>
                <div className="col-span-2"><label className="label">Company Owner</label><EmployeePicker value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} testid="company-owner-picker" restrictToTeam /></div>
              </div>
              <div>
                <label className="label">Tags</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {CRM_TAGS.map(t => (
                    <button key={t} type="button" onClick={() => toggleTag(t)} className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${(form.tags || []).includes(t) ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 text-slate-600 hover:border-[#1F7A6E]'}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div><label className="label">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" rows={3} /></div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="company-submit-btn">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {drawerCompany && (
        <EntityActivityDrawer
          entityType="company"
          entityId={drawerCompany.id}
          entityLabel={drawerCompany.name}
          subtitle={[drawerCompany.industry, drawerCompany.location || [drawerCompany.city, drawerCompany.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          metaRows={[
            { label: 'Industry', value: drawerCompany.industry },
            { label: 'Size', value: drawerCompany.size },
            { label: 'Website', value: drawerCompany.website },
            { label: 'Contacts', value: drawerCompany.contact_count || 0 },
            { label: 'Projects', value: drawerCompany.deal_count || 0 },
            { label: 'Owner', value: drawerCompany.owner },
          ]}
          onClose={() => setDrawerCompany(null)}
        />
      )}
    </div>
  );
}
